/**
 * Push notification provider with a default modal UI.
 *
 * Wraps the app and:
 *  1. Initializes FCM on mount (if user is authenticated)
 *  2. Shows a MUI Dialog when a foreground notification arrives
 *
 * ## Overriding the UI
 *
 * Pass a custom `onMessage` prop to handle notifications yourself:
 *
 *   <PushNotificationProvider onMessage={(payload) => showMyToast(payload)}>
 *     ...
 *   </PushNotificationProvider>
 *
 * Or replace this component entirely and call `initPushNotifications()` and
 * `setOnForegroundMessage()` directly from your own code.
 */

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Chip,
  Stack
} from '@mui/material';
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';
import { DASHBOARD_PATH, WATCHING_OVER_SECTION_ID } from '@/constants/pages';
import { invalidateCircleAlerts } from '@/hooks/use-circle-alerts.hook';
import {
  type PushNotificationPayload,
  initPushNotifications,
  setOnForegroundMessage,
  unregisterPushNotifications
} from '@/lib/push-notifications';

type PushNotificationContextValue = {
  /** Initialize push notifications (call after sign-in). */
  enable: () => Promise<void>;
  /** Unregister device token (call on sign-out). */
  disable: () => Promise<void>;
};

/** `data.type` of the push the backend sends when a circle concern is raised. */
const CONCERN_ALERT = 'CONCERN_ALERT';

const PushNotificationContext = createContext<PushNotificationContextValue>({
  enable: async () => {
    // No provider mounted — push is a no-op.
  },
  disable: async () => {
    // No provider mounted — push is a no-op.
  }
});

/**
 * Access `enable`/`disable` from anywhere under the provider.
 *
 * @public — the documented override hook (see the header comment); no in-repo
 * caller today, since sign-in/sign-out drive push directly via the lib.
 */
export const usePushNotifications = () => useContext(PushNotificationContext);

type Props = {
  children: ReactNode;
  /** Override the default modal handler with your own UI. */
  onMessage?: (payload: PushNotificationPayload) => void;
};

export function PushNotificationProvider({ children, onMessage }: Props) {
  const [notification, setNotification] =
    useState<PushNotificationPayload | null>(null);
  const [open, setOpen] = useState(false);

  const handleMessage = useCallback(
    (payload: PushNotificationPayload) => {
      // Refresh the header badge whatever UI handles the message.
      if (payload.data?.type === CONCERN_ALERT) {
        invalidateCircleAlerts();
      }
      if (onMessage) {
        onMessage(payload);
      } else {
        setNotification(payload);
        setOpen(true);
      }
    },
    [onMessage]
  );

  const enable = useCallback(async () => {
    setOnForegroundMessage(handleMessage);
    await initPushNotifications();
  }, [handleMessage]);

  const disable = useCallback(async () => {
    await unregisterPushNotifications();
  }, []);

  // Register the foreground handler unconditionally, and separately from init.
  // useSignIn calls initPushNotifications() directly on a fresh sign-in, at which
  // point permission is still 'default' — so gating registration on 'granted'
  // (as the auto-init effect below does) left foregroundHandler null and every
  // foreground notification was dropped until the next full page load.
  useEffect(() => {
    setOnForegroundMessage(handleMessage);
  }, [handleMessage]);

  // Auto-init on mount if notification permission was previously granted.
  // This re-registers the FCM token for returning signed-in users without
  // prompting for permission again. If the user isn't signed in, the
  // backend registration call fails silently (caught in push-notifications.ts).
  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      'Notification' in window &&
      Notification.permission === 'granted'
    ) {
      initPushNotifications().catch(() => {
        // Non-blocking: a failed re-init must not break app render.
      });
    }
  }, []);

  const handleClose = () => {
    setOpen(false);
    setNotification(null);
  };

  return (
    <PushNotificationContext.Provider value={{ enable, disable }}>
      {children}

      {/* Default modal UI — replace by passing onMessage prop */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        {notification && (
          <>
            <DialogTitle>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                {notification.title}
                {notification.data?.severity && (
                  <Chip
                    label={notification.data.severity}
                    size="small"
                    color={
                      notification.data.severity === 'CRITICAL'
                        ? 'error'
                        : notification.data.severity === 'HIGH'
                          ? 'warning'
                          : 'default'
                    }
                  />
                )}
                {notification.data?.isFireDrill === 'true' && (
                  <Chip label="TEST" size="small" color="info" />
                )}
              </Stack>
            </DialogTitle>
            <DialogContent>
              <DialogContentText>{notification.body}</DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose}>Dismiss</Button>
              <Button
                variant="contained"
                onClick={() => {
                  handleClose();
                  // Override this via onMessage for custom routing
                  window.location.assign(
                    notification.data?.type === CONCERN_ALERT
                      ? `${DASHBOARD_PATH}#${WATCHING_OVER_SECTION_ID}`
                      : DASHBOARD_PATH
                  );
                }}
              >
                View Details
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </PushNotificationContext.Provider>
  );
}
